import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingUp, DollarSign, AlertCircle, Clock, Sparkles } from "lucide-react";
import { PageHeader, StatusBadge, formatDate } from "@/lib/ui";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function Analytics() {
  const { currentOrgId } = useAuth();
  const [bucket, setBucket] = useState("month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(today());
  const [revenue, setRevenue] = useState(null);
  const [subSummary, setSubSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [integrations, setIntegrations] = useState(null);

  useEffect(() => {
    if (!currentOrgId) return;
    load();
    // integrations status is org-agnostic
    apiFetch("/integrations/status").then(setIntegrations).catch(() => {});
    // eslint-disable-next-line
  }, [currentOrgId, bucket]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ organization_id: currentOrgId, bucket });
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      const [rev, sub] = await Promise.all([
        apiFetch(`/reports/revenue?${params.toString()}`),
        apiFetch(`/reports/subscription-summary?organization_id=${currentOrgId}`),
      ]);
      setRevenue(rev);
      setSubSummary(sub);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  const openDrilldown = async (bucketDate) => {
    setDrillLoading(true);
    setDrilldown({ bucketDate, transactions: [] });
    try {
      const r = await apiFetch(`/reports/revenue/transactions?organization_id=${currentOrgId}&bucket=${bucket}&bucket_value=${bucketDate}`);
      setDrilldown({ bucketDate, transactions: r.transactions, start: r.start, end: r.end });
    } catch (e) { toast.error(e.message); }
    finally { setDrillLoading(false); }
  };

  const maxRevenue = useMemo(() => {
    if (!revenue?.series) return 0;
    return Math.max(1, ...revenue.series.map((s) => s.paid + s.outstanding + s.draft));
  }, [revenue]);

  const bucketLabel = (d) => {
    const dt = new Date(d);
    if (bucket === "day") return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    if (bucket === "week") return `Wk of ${dt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    return dt.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="analytics-page">
      <PageHeader title="Analytics" subtitle="Revenue over time, subscription health, and integration status." />

      {/* Subscription health */}
      {subSummary?.has_subscription && (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="analytics-sub-summary">
          <MetricCard icon={Sparkles} label="Subscription" value={subSummary.status?.replace(/_/g, " ") || "—"} highlight />
          <MetricCard icon={Clock} label="Trial days left" value={subSummary.trial_days_left ?? "—"} />
          <MetricCard icon={Clock} label="Period days left" value={subSummary.period_days_left ?? "—"} />
          <MetricCard icon={AlertCircle} label="Cancels at period end" value={subSummary.cancel_at_period_end ? "Yes" : "No"} />
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-4 grid grid-cols-1 md:grid-cols-[180px_180px_180px_auto] gap-3 items-end">
        <div>
          <Label className="text-xs">Bucket</Label>
          <Select value={bucket} onValueChange={setBucket}>
            <SelectTrigger className="mt-1" data-testid="analytics-bucket"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="week">Weekly</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1" data-testid="analytics-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1" data-testid="analytics-to" />
        </div>
        <Button data-testid="analytics-apply" onClick={load} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">Apply</Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard icon={DollarSign} label="Paid revenue" value={money(revenue?.totals?.paid)} highlight />
        <MetricCard icon={TrendingUp} label="Outstanding" value={money(revenue?.totals?.outstanding)} />
        <MetricCard icon={Clock} label="Draft entries" value={money(revenue?.totals?.draft)} />
      </div>

      {/* Chart + drill-down instructions */}
      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-display font-semibold text-lg tracking-tight">Revenue over time</h3>
            <p className="text-xs text-[#57534E]">Tap a bar to drill down into the transactions inside that {bucket}.</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#57534E]">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[#2C3A29] inline-block" /> Paid</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[#E8712C] inline-block" /> Outstanding</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[#F5EDE4] inline-block border border-[#E8E2D9]" /> Draft</span>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-[#57534E]">Loading…</div>
        ) : (revenue?.series || []).length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#E8E2D9] rounded-2xl">
            <p className="text-sm text-[#57534E]">No billing activity in this range yet.</p>
            <p className="text-xs text-[#A8A29E] mt-1">Log a billed visit or create a billing entry to see analytics here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="analytics-chart">
            <div className="flex items-end gap-2 min-w-full h-64 pt-2" style={{ minWidth: `${(revenue.series.length * 60)}px` }}>
              {revenue.series.map((s) => {
                const paidH = (s.paid / maxRevenue) * 100;
                const outH = (s.outstanding / maxRevenue) * 100;
                const draftH = (s.draft / maxRevenue) * 100;
                return (
                  <button key={s.date} data-testid={`chart-bar-${s.date}`} onClick={() => openDrilldown(s.date)}
                    className="flex-1 min-w-[48px] flex flex-col items-center group cursor-pointer">
                    <div className="w-full flex flex-col-reverse justify-end h-56 rounded-t-md overflow-hidden bg-[#FAF8F5] group-hover:opacity-90 transition-opacity relative"
                      title={`Paid ${money(s.paid)} · Outstanding ${money(s.outstanding)} · Draft ${money(s.draft)}`}>
                      {draftH > 0 && <div className="w-full bg-[#F5EDE4] border-t border-[#E8E2D9]" style={{ height: `${draftH}%` }} />}
                      {outH > 0 && <div className="w-full bg-[#E8712C]" style={{ height: `${outH}%` }} />}
                      {paidH > 0 && <div className="w-full bg-[#2C3A29]" style={{ height: `${paidH}%` }} />}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[#57534E] mt-2 group-hover:text-[#1C1917] whitespace-nowrap">{bucketLabel(s.date)}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Integrations status */}
      {integrations && (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6" data-testid="integrations-panel">
          <h3 className="font-display font-semibold text-lg tracking-tight mb-4">Integrations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <IntegrationRow name="Email (Resend)" cfg={integrations.email} env={integrations.email.env_var} note="Enables transactional emails: invitations, booking confirmations." />
            <IntegrationRow name="Stripe" cfg={{ configured: integrations.stripe.configured && integrations.stripe.webhook_configured }}
              envs={integrations.stripe.env_vars}
              note={`Secret: ${yn(integrations.stripe.configured)} · Publishable: ${yn(integrations.stripe.publishable_configured)} · Webhook: ${yn(integrations.stripe.webhook_configured)} · Mode: ${integrations.stripe.mode}`} />
            <IntegrationRow name="Google Calendar" cfg={integrations.google_calendar} envs={integrations.google_calendar.env_vars} note="Two-way sync of appointments to Google Calendar." />
            <IntegrationRow name="Outlook Calendar" cfg={integrations.outlook_calendar} envs={integrations.outlook_calendar.env_vars} note="Two-way sync of appointments to Microsoft 365 / Outlook." />
          </div>
          <p className="text-[11px] text-[#A8A29E] mt-3">Only the <b>configured/missing</b> state is shown here — secret values are never exposed to the browser or in API responses.</p>
        </div>
      )}

      {/* Drilldown dialog */}
      <Dialog open={!!drilldown} onOpenChange={(v) => !v && setDrilldown(null)}>
        <DialogContent className="bg-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Transactions {drilldown && (drilldown.start === drilldown.end ? `on ${formatDate(drilldown.start)}` : `from ${drilldown?.start || ""} to ${drilldown?.end || ""}`)}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#57534E]">Individual billing entries that roll up into this bucket.</DialogDescription>
          </DialogHeader>
          {drillLoading ? (
            <div className="py-8 text-center text-[#57534E]">Loading…</div>
          ) : drilldown?.transactions?.length === 0 ? (
            <div className="py-8 text-center text-[#57534E]">No transactions in this bucket.</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Client</th>
                    <th className="text-left px-3 py-2">Service</th>
                    <th className="text-right px-3 py-2">Hours × Rate</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E2D9]">
                  {(drilldown?.transactions || []).map((t) => (
                    <tr key={t.id} data-testid={`drill-txn-${t.id}`}>
                      <td className="px-3 py-2">{formatDate(t.service_date)}</td>
                      <td className="px-3 py-2">{t.clients ? `${t.clients.first_name} ${t.clients.last_name}` : "—"}</td>
                      <td className="px-3 py-2">{t.service}</td>
                      <td className="px-3 py-2 text-right text-[#57534E]">{Number(t.hours).toFixed(2)} × {money(t.rate)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{money(t.total)}</td>
                      <td className="px-3 py-2"><StatusBadge value={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDrilldown(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-2xl border p-5 ${highlight ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#57534E]">
        <Icon size={14} strokeWidth={1.75} />
        {label}
      </div>
      <div className="mt-2 font-display font-bold text-2xl tracking-tight capitalize">{value}</div>
    </div>
  );
}

function IntegrationRow({ name, cfg, env, envs, note }) {
  const ok = !!cfg?.configured;
  return (
    <div className="border border-[#E8E2D9] rounded-2xl p-4" data-testid={`integration-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold">{name}</div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${ok ? "bg-[#E8F0E4] text-[#2C3A29]" : "bg-[#FDDCDC] text-[#B02222]"}`}>
          {ok ? "Configured" : "Missing"}
        </span>
      </div>
      <p className="text-xs text-[#57534E] mt-1">{note}</p>
      <div className="text-[11px] text-[#A8A29E] mt-1 font-mono">
        {(envs || (env ? [env] : [])).join(" · ")}
      </div>
    </div>
  );
}

function yn(v) { return v ? "✓" : "✗"; }
